import React, { useState, useEffect } from 'react';
import {
  Drawer,
  Form,
  Button,
  DatePicker,
  SelectPicker,
  Message,
  InputNumber,
  Toggle
} from 'rsuite';
import { useDispatch } from 'react-redux';
import { format, isAfter, isBefore } from 'date-fns';
import { updateSetting } from '@/services/setting.service';
import { fetchHolidays } from '@/services/feature.service';
import { useTranslation } from 'react-i18next';


interface EditHolidayDrawerProps {
  open: boolean;
  onClose: () => void;
  toaster: any;
  holidayData: any;
}

const EditHolidayDrawer: React.FC<EditHolidayDrawerProps> = ({
  open,
  onClose,
  toaster,
  holidayData
}) => {
  const dispatch = useDispatch();
  const [formValue, setFormValue] = useState({
    name: 'Holidays',
    label: '',
    type: '',
    date_debut: null,
    date_fin: null,
    islamic: false,
    reccurent: false,
    daysPerYear: null
  });
  const [formError, setFormError] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && holidayData) {
      setFormValue({
        name: 'Holidays',
        label: holidayData.label || '',
        type: holidayData.type || '',
        date_debut: holidayData.date_debut ? new Date(holidayData.date_debut) : null,
        date_fin: holidayData.date_fin ? new Date(holidayData.date_fin) : null,
        islamic: holidayData.islamic || false,
        reccurent: holidayData.reccurent || false,
        daysPerYear: holidayData.daysPerYear || null
      });
    }
  }, [open, holidayData]);
  const handleSubmit = async () => {
    if (!formValue.label) {
      setFormError({ label: 'Holiday name is required' });
      return;
    }

    if (!formValue.type) {
      setFormError({ type: 'Holiday type is required' });
      return;
    }

    if (!formValue.date_debut) {
      setFormError({ date_debut: 'Start date is required' });
      return;
    }

    if (!formValue.date_fin) {
      setFormError({ date_fin: 'End date is required' });
      return;
    }

    if (isAfter(formValue.date_debut, formValue.date_fin)) {
      setFormError({
        date_fin: 'End date must be after start date'
      });
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        name: 'Holidays',
        label: formValue.label,
        type: formValue.type,
        date_debut: format(formValue.date_debut, "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"),
        date_fin: format(formValue.date_fin, "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"),
        islamic: formValue.islamic,
        reccurent: formValue.reccurent,
        daysPerYear: formValue.daysPerYear ? Number(formValue.daysPerYear) : null,
        grades: []
      };

      const response = await updateSetting(holidayData._id, payload);
      console.log('response', response);

      await dispatch(fetchHolidays());

      toaster.push(
        <Message type="success" duration={3000}>
          {t('Holiday updated successfully!')}
        </Message>,
        { placement: 'topCenter' }
      );

      onClose();
    } catch (error) {
      console.error('Update Error:', error);

      toaster.push(
        <Message type="error" duration={3000}>
          {t('Error updating holiday:')} {t(error.message)}
        </Message>,
        { placement: 'topCenter' }
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartDateChange = date => {
    setFormValue(prev => {
      const updatedEndDate = prev.date_fin && isAfter(prev.date_fin, date) ? prev.date_fin : date;

      return {
        ...prev,
        date_debut: date,
        date_fin: updatedEndDate
      };
    });
  };

  const handleEndDateChange = date => {
    setFormValue(prev => {
      if (prev.date_debut && isAfter(prev.date_debut, date)) {
        return prev;
      }
      return {
        ...prev,
        date_fin: date
      };
    });
  };
  const { t } = useTranslation();
  const typeOptions = [
    { label: t('PAID'), value: 'paid' },
    { label: t('UNPAID'), value: 'unpaid' },
    { label: t('RELIGIOUS'), value: 'religious' },
    { label: t('NATIONAL'), value: 'national' }
  ];

  return (
    <Drawer backdrop="static" size="sm" placement="right" onClose={onClose} open={open}>
      <Drawer.Header>
        <Drawer.Title>{t('Edit Holiday')}</Drawer.Title>
        <Drawer.Actions>
          <Button onClick={handleSubmit} appearance="primary" loading={submitting}>
            {t('Save')}
          </Button>
          <Button onClick={onClose} appearance="subtle">
            {t('Cancel')}
          </Button>
        </Drawer.Actions>
      </Drawer.Header>

      <Drawer.Body>
        <Form
          fluid
          formValue={formValue}
          onChange={value => setFormValue(value)}
          formError={formError}
        >
          <Form.Group>
            <Form.ControlLabel>{t('Start Date')}</Form.ControlLabel>
            <DatePicker
              block
              format="yyyy-MM-dd"
              value={formValue.date_debut}
              onChange={handleStartDateChange}
              onSelect={handleStartDateChange}
              disabledDate={date => formValue.date_fin && isAfter(date, formValue.date_fin)}
              placement="auto"
            />
            {formError.date_debut && <Form.ErrorMessage>{formError.date_debut}</Form.ErrorMessage>}
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('End Date')}</Form.ControlLabel>
            <DatePicker
              block
              format="yyyy-MM-dd"
              value={formValue.date_fin}
              onChange={handleEndDateChange}
              onSelect={handleEndDateChange}
              disabledDate={date => formValue.date_debut && isBefore(date, formValue.date_debut)}
              placement="auto"
            />
            {formError.date_fin && <Form.ErrorMessage>{formError.date_fin}</Form.ErrorMessage>}
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('Holiday Name')}</Form.ControlLabel>
            <Form.Control
              name="label"
              placeholder="Enter holiday name"
              errorMessage={formError.label}
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('Holiday Type')}</Form.ControlLabel>
            <Form.Control
              name="type"
              accepter={SelectPicker}
              data={typeOptions}
              searchable={false}
              placeholder={t("Select type")}
              block
              errorMessage={formError.type}
              placement="auto"
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('Islamic Holiday')}</Form.ControlLabel>
            <Form.Control
              name="islamic"
              accepter={Toggle}
              checked={formValue.islamic}
              onChange={checked => {
                setFormValue(prev => ({
                  ...prev,
                  islamic: checked,
                  reccurent: checked ? false : prev.reccurent
                }));
              }}
              checkedChildren={t("Yes")}
              unCheckedChildren={t("No")}
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('Recurrent Holiday')}</Form.ControlLabel>
            <Form.Control
              name="reccurent"
              accepter={Toggle}
              checked={formValue.reccurent}
              onChange={checked => {
                setFormValue(prev => ({
                  ...prev,
                  reccurent: checked,
                  islamic: checked ? false : prev.islamic
                }));
              }}
              checkedChildren={t("Yes")}
              unCheckedChildren={t("No")}
            />
          </Form.Group>

          {formValue.islamic && (
            <Form.Group>
              <Form.ControlLabel>{t('Days Per Year (Optional)')}</Form.ControlLabel>
              <Form.Control
                name="daysPerYear"
                accepter={InputNumber}
                min={0}
                value={formValue.daysPerYear}
                onChange={value => {
                  setFormValue(prev => ({
                    ...prev,
                    daysPerYear: value
                  }));
                }}
                placeholder="Enter days per year if applicable"
              />
            </Form.Group>
          )}
        </Form>
      </Drawer.Body>
    </Drawer>
  );
};

export default EditHolidayDrawer;
